export interface AppThemeColors {
  primary: string
  primaryForeground: string
  secondary: string
  secondaryForeground: string
  accent: string
  accentForeground: string
  ring?: string
  link?: string
  linkForeground?: string
  selection?: string
  selectionForeground?: string
}

/**
 * Convert hex color to HSL format
 * @param hex - Hex color string (with or without #)
 * @returns HSL string in format "h s% l%"
 */
function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  const hDeg = Math.round(h * 360)
  const sPct = Math.round(s * 100)
  const lPct = Math.round(l * 100)

  return `${hDeg} ${sPct}% ${lPct}%`
}

/**
 * Normalize color value: if hex, convert to HSL; otherwise return as-is
 * @param value - Color value (hex or HSL string)
 * @returns HSL string in format "h s% l%"
 */
function normalizeColorValue(value: string): string {
  // Check if it's a hex color (6 chars, all hex digits, optionally prefixed with #)
  if (/^#?[0-9a-fA-F]{6}$/.test(value)) {
    return hexToHsl(value)
  }
  return value
}

export type AppThemeOverride = Partial<AppThemeColors>

/**
 * Convert app theme to CSS custom properties for inline style
 * @param themeOverrides - Partial theme object with override colors
 * @returns React.CSSProperties with CSS variable names
 */
export function getCssVars(themeOverrides?: Partial<AppThemeColors> | null): Record<string, string> {
  const overrides = themeOverrides || {}

  // Normalize hex colors in overrides to HSL
  const normalizedOverrides = Object.entries(overrides).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key as keyof AppThemeColors] = normalizeColorValue(String(value))
      }
      return acc
    },
    {} as Partial<AppThemeColors>
  )

  const hasCustomTheme = Object.keys(normalizedOverrides).length > 0
  if (!hasCustomTheme) {
    // Do not set runtime CSS vars when there is no custom theme.
    // This allows :root/.dark defaults from index.scss to drive light/dark palettes.
    return {}
  }

  const primary = normalizedOverrides.primary
  const primaryForeground = normalizedOverrides.primaryForeground

  const ringValue = normalizedOverrides.ring ?? primary
  const linkValue = normalizedOverrides.link ?? primary
  const selectionValue = normalizedOverrides.selection ?? primary
  const selectionForegroundValue = normalizedOverrides.selectionForeground ?? primaryForeground

  const cssVars: Record<string, string> = {}
  const assignIfPresent = (name: string, value: string | undefined) => {
    if (value !== undefined && value !== null && value !== '') {
      cssVars[name] = value
    }
  }

  assignIfPresent('--primary', primary)
  assignIfPresent('--primary-foreground', primaryForeground)
  assignIfPresent('--secondary', normalizedOverrides.secondary)
  assignIfPresent('--secondary-foreground', normalizedOverrides.secondaryForeground)
  assignIfPresent('--accent', normalizedOverrides.accent)
  assignIfPresent('--accent-foreground', normalizedOverrides.accentForeground)
  assignIfPresent('--ring', ringValue)
  assignIfPresent('--link', linkValue)
  assignIfPresent('--link-foreground', normalizedOverrides.linkForeground)
  assignIfPresent('--selection', selectionValue)
  assignIfPresent('--selection-foreground', selectionForegroundValue)

  return cssVars
}
